import React, { useState, useEffect } from 'react';
import { Check, Globe, CreditCard, Shield, ArrowRight, Minus, Plus, Target, Zap } from 'lucide-react';
import { DOMAINS } from '../config/domains';

interface OrderData {
  domain: string;
  selectedSites: number;
  monthlyPrice: number;
  customerInfo: {
    name: string;
    email: string;
    company: string;
    phone: string;
  };
}

const OrderPage: React.FC = () => {
  const [selectedSites, setSelectedSites] = useState(10);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: ''
  });
  const [domain, setDomain] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSelectedDomains, setShowSelectedDomains] = useState(false);
  const [selectedDomainsList, setSelectedDomainsList] = useState<string[]>([]);
  const [showDomainSelector, setShowDomainSelector] = useState(false);
  const [orderType, setOrderType] = useState<'citations' | 'keywords'>('citations');
  const [keywordOrderData, setKeywordOrderData] = useState<any>(null);

  const pricePerSite = 4;
  const monthlyPrice = selectedSites * pricePerSite;
  const setupFee = 0; // Free setup
  const totalPrice = monthlyPrice + setupFee;

  // Get domain from URL params and check for keyword order
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const domainParam = urlParams.get('domain');
    const typeParam = urlParams.get('type');
    
    if (domainParam) {
      setDomain(domainParam);
    }
    
    if (typeParam === 'keywords') {
      setOrderType('keywords');
      const keywordData = localStorage.getItem('keywordOrderData');
      if (keywordData) {
        const parsed = JSON.parse(keywordData);
        setKeywordOrderData(parsed);
        setDomain(parsed.domain);
        // Set sites based on number of keywords
        setSelectedSites(Math.min(parsed.keywords?.length || 10, 50));
      }
    }
  }, []);

  // Get selected domains (either manually selected or random)
  const getSelectedDomains = () => {
    if (selectedDomainsList.length === selectedSites) {
      return selectedDomainsList;
    }
    const shuffled = [...DOMAINS].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, selectedSites);
  };

  // Calculate growth potential based on site count
  const calculateGrowthPotential = () => {
    const baseTrafficIncrease = Math.min(selectedSites * 0.8, 60); // Max 60%
    const baseDRIncrease = Math.min(selectedSites * 0.15, 25); // Max 25 points
    const baseRankingImprovement = Math.min(selectedSites * 0.3, 20); // Max 20 positions
    
    return {
      trafficIncrease: `${Math.round(baseTrafficIncrease)}%`,
      drIncrease: `+${Math.round(baseDRIncrease)}`,
      rankingImprovement: `${Math.round(baseRankingImprovement)} positions`,
      estimatedValue: `$${Math.round(selectedSites * 15)}/month` // Estimated SEO value
    };
  };

  const growthPotential = calculateGrowthPotential();

  const toggleDomainSelection = (domain: string) => {
    if (selectedDomainsList.includes(domain)) {
      setSelectedDomainsList(prev => prev.filter(d => d !== domain));
      setSelectedSites(selectedDomainsList.length - 1);
    } else if (selectedDomainsList.length < 301) {
      setSelectedDomainsList(prev => [...prev, domain]);
      setSelectedSites(selectedDomainsList.length + 1);
    }
  };

  const handleSiteCountChange = (change: number) => {
    const newCount = Math.max(1, Math.min(301, selectedSites + change));
    setSelectedSites(newCount);
  };

  const handleInputChange = (field: string, value: string) => {
    setCustomerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckout = async () => {
    // Validate form
    if (!customerInfo.name || !customerInfo.email || !domain) {
      alert('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domain,
          selectedSites,
          monthlyPrice,
          customerInfo,
          selectedDomains: getSelectedDomains(),
          orderType,
          keywordOrderData: orderType === 'keywords' ? keywordOrderData : null
        }),
      });

      const result = await response.json();
      
      if (result.success && result.data.checkout_url) {
        // For keyword orders, start posting process after successful checkout
        if (orderType === 'keywords' && keywordOrderData) {
          // Store order data for posting after payment
          localStorage.setItem('pendingKeywordOrder', JSON.stringify({
            keywords: keywordOrderData.keywords,
            domain,
            businessName: customerInfo.name,
            targetUrl: `https://${domain}`,
            selectedSites,
            customerInfo
          }));
        }
        
        // Redirect to Stripe checkout
        window.location.href = result.data.checkout_url;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to start keyword posting process
  const startKeywordPosting = async () => {
    if (!keywordOrderData) return;
    
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/posting/post-to-sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywords: keywordOrderData.keywords,
          domain,
          businessName: customerInfo.name,
          targetUrl: `https://${domain}`,
          selectedSites
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        alert(`Successfully posted ${result.successCount} keywords to ${result.successCount} sites!`);
        // Redirect to success page or dashboard
        window.location.href = '/success';
      } else {
        alert('Failed to post keywords. Please try again.');
      }
    } catch (error) {
      console.error('Posting error:', error);
      alert('Something went wrong while posting. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text">BipSEO</h1>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/scanner" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Free Scanner
              </a>
              <a href="/network" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Our Network
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            Order Citation Campaign
          </h1>
          <p className="text-xl text-gray-600">
            Boost your SEO with high-authority citations from our premium network
          </p>
          {domain && (
            <div className="mt-4 inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700 px-6 py-3 rounded-full border border-blue-200">
              <Globe className="w-5 h-5" />
              Campaign for: <strong className="text-purple-700">{domain}</strong>
            </div>
          )}
        </div>

        {/* Growth Potential Banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">🚀 Projected Growth with {selectedSites} Citations</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-2xl font-bold">{growthPotential.trafficIncrease}</div>
                <div className="text-green-100 text-sm">Traffic Increase</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-2xl font-bold">{growthPotential.drIncrease}</div>
                <div className="text-green-100 text-sm">DR Score Boost</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-2xl font-bold">{growthPotential.rankingImprovement}</div>
                <div className="text-green-100 text-sm">Ranking Improvement</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <div className="text-2xl font-bold">{growthPotential.estimatedValue}</div>
                <div className="text-green-100 text-sm">Estimated SEO Value</div>
              </div>
            </div>
          </div>
        </div>

        {/* Keyword Order Section */}
        {orderType === 'keywords' && keywordOrderData && (
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-lg border border-purple-200 p-8 mb-8">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-6 flex items-center gap-3">
              <Target className="w-6 h-6 text-purple-600" />
              Keyword Optimization Campaign
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Selected Keywords */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-500" />
                  Selected Keywords for Optimization
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {keywordOrderData.keywords?.slice(0, 10).map((keyword: any, index: number) => (
                    <div key={index} className="p-3 bg-white rounded-lg border border-purple-200 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{keyword.keyword}</p>
                          <p className="text-sm text-purple-600">
                            Position: {keyword.position} | Traffic: {keyword.traffic?.toLocaleString() || 0}
                          </p>
                        </div>
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                          Score: {keyword.opportunity_score}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Campaign Details */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Campaign Details</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg border border-purple-200">
                    <h5 className="font-medium text-gray-900 mb-2">What You'll Get:</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Optimized content for each keyword</li>
                      <li>• Posted to {selectedSites} high-authority sites</li>
                      <li>• Contextual backlinks to your domain</li>
                      <li>• Monthly progress reports</li>
                      <li>• Keyword ranking tracking</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg border border-purple-200">
                    <h5 className="font-medium text-gray-900 mb-2">Expected Results:</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="font-semibold text-purple-600">
                          {keywordOrderData.analysis?.estimated_traffic_gain?.toLocaleString() || 0}
                        </div>
                        <div className="text-gray-600">Traffic Gain</div>
                      </div>
                      <div>
                        <div className="font-semibold text-blue-600">
                          ${keywordOrderData.analysis?.estimated_value_gain?.toLocaleString() || 0}
                        </div>
                        <div className="text-gray-600">Value Gain</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration */}
          <div className="lg:col-span-2 space-y-8">
            {/* Site Selection */}
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-200 p-8">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
                Select Citation Sites
              </h3>
              
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Number of Citation Sites</div>
                  <div className="text-2xl font-bold text-gray-900">{selectedSites} sites</div>
                  <div className="text-sm text-gray-500">from our 301-domain network</div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleSiteCountChange(-1)}
                    disabled={selectedSites <= 1}
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  
                  <div className="w-20 text-center">
                    <input
                      type="number"
                      min="1"
                      max="301"
                      value={selectedSites}
                      onChange={(e) => setSelectedSites(Math.max(1, Math.min(301, parseInt(e.target.value) || 1)))}
                      className="w-full text-center border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <button
                    onClick={() => handleSiteCountChange(1)}
                    disabled={selectedSites >= 301}
                    className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Quick Select Buttons */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[10, 25, 50, 100].map((count) => (
                  <button
                    key={count}
                    onClick={() => setSelectedSites(count)}
                    className={`py-3 px-4 rounded-xl border-2 font-semibold transition-all transform hover:scale-105 ${
                      selectedSites === count
                        ? 'border-blue-500 bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                        : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                    }`}
                  >
                    {count} sites
                  </button>
                ))}
              </div>

              {/* Individual Domain Selection */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Choose Specific Domains (Optional)</h4>
                  <button
                    onClick={() => setShowDomainSelector(!showDomainSelector)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                  >
                    {showDomainSelector ? 'Hide' : 'Select'} Individual Sites
                  </button>
                </div>
                
                {showDomainSelector && (
                  <div className="bg-gray-50 rounded-xl p-6 max-h-80 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {DOMAINS.slice(0, 50).map((domainName) => (
                        <label key={domainName} className="flex items-center gap-3 p-3 hover:bg-white rounded-lg cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedDomainsList.includes(domainName)}
                            onChange={() => toggleDomainSelection(domainName)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 flex items-center gap-2">
                            <Globe className="w-3 h-3 text-blue-500" />
                            {domainName}
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-4 text-center">
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                        Load More Domains ({DOMAINS.length - 50} remaining)
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Selected Domains */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={() => setShowSelectedDomains(!showSelectedDomains)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showSelectedDomains ? 'Hide' : 'Preview'} Selected Domains
                </button>
                
                {showSelectedDomains && (
                  <div className="mt-4 max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {getSelectedDomains().map((domain, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                          <Globe className="w-3 h-3 text-blue-500" />
                          <span className="truncate">{domain}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Customer Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={customerInfo.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {!domain && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Domain *
                  </label>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl shadow-xl border border-purple-200 p-8 sticky top-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                Order Summary
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-800 font-medium">Citation Sites</span>
                  <span className="font-bold text-gray-900">{selectedSites} sites</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-800 font-medium">Price per site</span>
                  <span className="font-bold text-gray-900">${pricePerSite}/month</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-800 font-medium">Setup Fee</span>
                  <span className="font-bold text-green-600">FREE</span>
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-gray-900">Monthly Total</span>
                    <span className="text-gray-900">${monthlyPrice}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Billed monthly, cancel anytime
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Check className="w-4 h-4 text-green-500" />
                  High-authority citations
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Check className="w-4 h-4 text-green-500" />
                  Monthly progress reports
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Check className="w-4 h-4 text-green-500" />
                  Real-time dashboard access
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Check className="w-4 h-4 text-green-500" />
                  24/7 customer support
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                disabled={isProcessing || !customerInfo.name || !customerInfo.email || !domain}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-3"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Proceed to Checkout
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
                <Shield className="w-4 h-4" />
                Secured by Stripe
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPage;
